import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subscription} from "rxjs";
import {GameServerMessage} from "../interfaces/game-server-message";
import {TimerButtonState} from "../enums/timer-button-state";
import {MqttClientService} from "./mqtt-client.service";
import moment from 'moment';
import {MetaTagsService} from "./meta-tags.service";
import {LargeMessageService} from "./large-message.service";
import {LargeMessageTheme} from "../enums/large-message-theme";
import {DataService} from "./data.service";
import {Store} from "@ngrx/store";
import {AppState} from "../store";
import {getActiveClassId} from "../store/selectors/teacher.selector";
import {getActiveSchoolClassIdStudent} from "../store/selectors/student.selector";
import {GameSessionService} from "./game-session.service";

@Injectable({
  providedIn: 'root'
})
export class TimerButtonService {

  private readonly INTERVAL_DELAY = 1000; // ms = 1sec
  private _isSubscribed = false;
  private readonly defaultState = {state: TimerButtonState.PLAY, timerValue: 123};
  private _newStateSubject: BehaviorSubject<GameServerMessage> = new BehaviorSubject(this.defaultState);

  private countdownInterval = -1;

  private mqttSubscriptions: Subscription[] = [];


  constructor(
    private mqttClient: MqttClientService,
    private metaTagsService: MetaTagsService,
    private largeMessageService: LargeMessageService,
    private data: DataService,
    private store: Store<AppState>,
    private gameSessionService: GameSessionService
  ) {
    // this.gameSessionService.gameSessionActive$.subscribe((state) => {
    //   if(!state) {
    //     this.toggleCountdownInterval(false);
    //     this.resetTitle();
    //   }
    // })
  }

  subscribe(): void {
    this.store.select(getActiveClassId()).subscribe((schoolClassId) => {
      // Case: Teacher logs in and loads game Timer

      if (schoolClassId && schoolClassId > 0) {

        this.data.getGameTimer(schoolClassId).subscribe(response => {
          if (response) {
            this.startTimerWithEndTime(response.endAt ?? "");
          } else {
            this.toggleCountdownInterval(false);
            this.resetTitle();
          }
        });
      } else {

        // Case: This is a student logging in
        this.store.select(getActiveSchoolClassIdStudent()).subscribe((schoolClassId) => {
          if (schoolClassId) {
            this.data.getGameTimer(parseInt(schoolClassId)).subscribe(response => {
              if (response) {

                this.startTimerWithEndTime(response.endAt ?? "");
              }
            });
          }
        });
      }
    });

    this.mqttSubscriptions.push(this.mqttClient.gameSessionTimeSubject.subscribe(response => {
      if(response.length) {

        this.startTimerWithEndTime(response);
      }
    }));

    this._isSubscribed = true;

    this.mqttSubscriptions.push(this.mqttClient.gameSessionPauseSubject.subscribe(response => {
      if(response) {
        this.onMessage({state: TimerButtonState.PAUSE, timerValue: 0});
      }
    }));

    this.mqttSubscriptions.push(this.mqttClient.gameSessionEndSubject.subscribe((response) => {
      this.onMessage({state: TimerButtonState.STOP, timerValue: 0});
    }));

  }

  startTimerWithEndTime(endTime: string) {
    // Get Time left from Response in milliseconds
    let end = moment(endTime, "DD.MM.YYYY HH:mm:ss");
    let now = moment(new Date());
    let duration = moment.duration(end.diff(now)).asSeconds();
    this.onMessage({state: TimerButtonState.PLAY, timerValue: duration});

  }

  unsubscribe(): void {
    this.mqttSubscriptions.forEach((el) => el.unsubscribe());
    this.mqttSubscriptions = [];
    this._isSubscribed = false;
    this.onDisconnect();
  }

  onUpdate(): Observable<GameServerMessage> {
    return this._newStateSubject;
  }

  private onDisconnect(): void {
    this.toggleCountdownInterval(false);
    this.resetTitle();
  }

  private onMessage(msg: any): void {
    // console.log(msg);
    const validatedMessage = this.validateMessage(msg);

    const newState: TimerButtonState = validatedMessage.state;

    if(newState === TimerButtonState.PLAY) {
      this.toggleCountdownInterval(true);
    } else {
      this.toggleCountdownInterval(false);
    }

    this._newStateSubject.next(validatedMessage);
  }

  private validateMessage(msg: any): GameServerMessage {
    return msg;
  }

  private toggleCountdownInterval(state: boolean): void {
    clearInterval(this.countdownInterval);
    if(state) {
      this.countdownInterval = window.setInterval(this.countdown.bind(this), this.INTERVAL_DELAY);
    }
  }

  private countdown() {
    const currentState = {...this._newStateSubject.value};
    if(currentState.timerValue > 0) {
      currentState.timerValue--;
      this.setTimerValueInDocumentTitle(currentState.timerValue);
      this._newStateSubject.next(currentState);

    } else {
      this._newStateSubject.next({state: TimerButtonState.STOP, timerValue: 0});
      this.gameSessionService.endGameSession();
      this.toggleCountdownInterval(false);
      this.onTimeOut();
    }
  }

  private setTimerValueInDocumentTitle(value: number): void {
    value = Math.floor(value);
    if(value < 0) value = 0;
    let minutes = Math.floor(value / 60);
    let minutesStr = minutes < 10 ? `0${minutes}` : minutes;
    let seconds = value % 60;
    let secondsStr = (seconds < 10) ? `0${seconds}` : seconds;

    window.document.title = `${minutesStr}:${secondsStr} ${this.metaTagsService.getCurrentPageTitle()}`;
  }

  private resetTitle(): void {
    window.document.title = this.metaTagsService.getCurrentPageTitle();
  }

  private onTimeOut(): void {

    this.largeMessageService.showLargeMessage({
      lottiePath: "/assets/lottie-animations/game_time_overlay.json",
      headline: "<em>Game Session</em> ist<br/> jetzt vorbei",
      text: "",
      theme: LargeMessageTheme.BLUE,
      primaryButtonLabel: "Ok",
      primaryButtonCallback: () => {}
    })
  }


}
